/**
 * Types for context uploads (files and directories) in chat
 * Used by both LandingPage and ChatDisplay for file/folder uploads
 */

export type UploadStatus = 'pending' | 'uploading' | 'completed' | 'error'

// Base interface for common upload properties
export interface BaseUpload {
  id: number
  name: string
  size: string
  uploadStatus: UploadStatus
  s3Path?: string
  uploadProgress?: number
  error?: string
}

// Individual file uploads (no tar archive needed)
export interface UploadedFile extends BaseUpload {
  type: 'file'
  file?: File // The original file (cleared after upload)
}

// Directory/repository uploads (compressed to tar.gz)
export interface UploadedDirectory extends BaseUpload {
  type: 'directory'
  originalFilename: string // Original folder name
  tarFile?: File // The compressed tar.gz (cleared after upload)
}

// Union type for all context uploads
export type ContextUploadItem = UploadedFile | UploadedDirectory

// API format for sending context uploads to backend
export interface ContextUploadApiItem {
  file_upload?: { file_name: string; s3_path: string }
  directory_upload?: { directory_name: string; s3_path: string }
}

export function buildCommandContextUploadFromApiItems(contextUploads: ContextUploadApiItem[]) {
  const files = contextUploads.flatMap(item =>
    item.file_upload
      ? [{ fileName: item.file_upload.file_name, fileS3Path: item.file_upload.s3_path }]
      : []
  )

  const directories = contextUploads.flatMap(item =>
    item.directory_upload
      ? [
          {
            directoryName: item.directory_upload.directory_name,
            directoryS3Path: item.directory_upload.s3_path
          }
        ]
      : []
  )

  if (files.length === 0 && directories.length === 0) {
    return undefined
  }

  return {
    instructions: '',
    files,
    ...(directories.length > 0 ? { directories } : {})
  }
}
